// Parameter diff + display labels. Used by ChatPanel to render the
// "what changed" chips under the assistant explanation.
//
// The backend may emit its own `diff` array (Hub plans to); when it
// doesn't, we compute a client-side diff from the previous and new
// Parameters objects via shallow + deep path walking.

import type { Parameters } from '../types/parameters';
import { hexToColourName } from './colour-name';

/** One entry in a parameter diff — a dotted path plus before/after values. */
export interface ParameterDiffEntry {
  parameter: string;
  before: unknown;
  after: unknown;
}

export interface ParameterDisplay {
  /** User-facing label, e.g. "Iris colour". */
  label: string;
  /** Whether this field is a hex colour (affects chip rendering). */
  isColor: boolean;
  /** Group for grouping chips visually. */
  group:
    | 'Anatomy'
    | 'Catchlight'
    | 'Reflected scene'
    | 'Colour tint'
    | 'Mood'
    | 'Gaze'
    | 'Lighting'
    | 'Technical';
  /** Format a value for display. */
  format: (v: unknown) => string;
}

/** `parameter_path` → display metadata. Keys are dotted paths. */
export const PARAMETER_LABELS: Record<string, ParameterDisplay> = {
  iris_color: {
    label: 'Iris colour',
    isColor: true,
    group: 'Anatomy',
    format: (v) =>
      typeof v === 'string' ? `${v} · ${hexToColourName(v).label}` : String(v),
  },
  iris_detail: {
    label: 'Iris detail',
    isColor: false,
    group: 'Anatomy',
    format: (v) => (typeof v === 'number' ? v.toFixed(2) : String(v)),
  },
  iris_pattern: {
    label: 'Iris pattern',
    isColor: false,
    group: 'Anatomy',
    format: (v) => String(v),
  },
  'limbal_ring.enabled': {
    label: 'Limbal ring',
    isColor: false,
    group: 'Anatomy',
    format: (v) => (v ? 'on' : 'off'),
  },
  'limbal_ring.intensity': {
    label: 'Limbal ring intensity',
    isColor: false,
    group: 'Anatomy',
    format: (v) => (typeof v === 'number' ? v.toFixed(2) : String(v)),
  },
  pupil_size: {
    label: 'Pupil size',
    isColor: false,
    group: 'Anatomy',
    format: (v) => (typeof v === 'number' ? v.toFixed(2) : String(v)),
  },
  pupil_shape: {
    label: 'Pupil shape',
    isColor: false,
    group: 'Anatomy',
    format: (v) => String(v),
  },
  sclera_tint: {
    label: 'Sclera tint',
    isColor: true,
    group: 'Anatomy',
    format: (v) => String(v),
  },
  sclera_veining: {
    label: 'Sclera veining',
    isColor: false,
    group: 'Anatomy',
    format: (v) => (typeof v === 'number' ? v.toFixed(2) : String(v)),
  },
  eyelid_shape: {
    label: 'Eyelid shape',
    isColor: false,
    group: 'Anatomy',
    format: (v) => String(v),
  },
  lid_shadow: {
    label: 'Lid shadow',
    isColor: false,
    group: 'Anatomy',
    format: (v) => (typeof v === 'number' ? v.toFixed(2) : String(v)),
  },
  lash_density: {
    label: 'Lash density',
    isColor: false,
    group: 'Anatomy',
    format: (v) => (typeof v === 'number' ? v.toFixed(2) : String(v)),
  },
  lash_length: {
    label: 'Lash length',
    isColor: false,
    group: 'Anatomy',
    format: (v) => (typeof v === 'number' ? v.toFixed(2) : String(v)),
  },
  lash_curve: {
    label: 'Lash curve',
    isColor: false,
    group: 'Anatomy',
    format: (v) => String(v),
  },

  'catchlight.enabled': {
    label: 'Catchlight',
    isColor: false,
    group: 'Catchlight',
    format: (v) => (v ? 'on' : 'off'),
  },
  'catchlight.x': {
    label: 'Catchlight x',
    isColor: false,
    group: 'Catchlight',
    format: (v) => (typeof v === 'number' ? v.toFixed(2) : String(v)),
  },
  'catchlight.y': {
    label: 'Catchlight y',
    isColor: false,
    group: 'Catchlight',
    format: (v) => (typeof v === 'number' ? v.toFixed(2) : String(v)),
  },
  'catchlight.size': {
    label: 'Catchlight size',
    isColor: false,
    group: 'Catchlight',
    format: (v) => (typeof v === 'number' ? v.toFixed(2) : String(v)),
  },
  'catchlight.shape': {
    label: 'Catchlight shape',
    isColor: false,
    group: 'Catchlight',
    format: (v) => String(v),
  },
  'catchlight.color': {
    label: 'Catchlight colour',
    isColor: true,
    group: 'Catchlight',
    format: (v) => String(v),
  },
  'catchlight.intensity': {
    label: 'Catchlight intensity',
    isColor: false,
    group: 'Catchlight',
    format: (v) => (typeof v === 'number' ? v.toFixed(2) : String(v)),
  },
  'catchlight.blur': {
    label: 'Catchlight blur',
    isColor: false,
    group: 'Catchlight',
    format: (v) => (typeof v === 'number' ? v.toFixed(2) : String(v)),
  },
  'catchlight.count': {
    label: 'Catchlight count',
    isColor: false,
    group: 'Catchlight',
    format: (v) => String(v),
  },
  'catchlight.count_pattern': {
    label: 'Catchlight pattern',
    isColor: false,
    group: 'Catchlight',
    format: (v) => String(v),
  },

  'reflected_scene.enabled': {
    label: 'Reflected scene',
    isColor: false,
    group: 'Reflected scene',
    format: (v) => (v ? 'on' : 'off'),
  },
  'reflected_scene.preset': {
    label: 'Scene preset',
    isColor: false,
    group: 'Reflected scene',
    format: (v) => String(v),
  },
  'reflected_scene.x': {
    label: 'Scene x',
    isColor: false,
    group: 'Reflected scene',
    format: (v) => (typeof v === 'number' ? v.toFixed(2) : String(v)),
  },
  'reflected_scene.y': {
    label: 'Scene y',
    isColor: false,
    group: 'Reflected scene',
    format: (v) => (typeof v === 'number' ? v.toFixed(2) : String(v)),
  },
  'reflected_scene.size': {
    label: 'Scene size',
    isColor: false,
    group: 'Reflected scene',
    format: (v) => (typeof v === 'number' ? v.toFixed(2) : String(v)),
  },
  'reflected_scene.opacity': {
    label: 'Scene opacity',
    isColor: false,
    group: 'Reflected scene',
    format: (v) => (typeof v === 'number' ? v.toFixed(2) : String(v)),
  },
  'reflected_scene.tint': {
    label: 'Scene tint',
    isColor: true,
    group: 'Reflected scene',
    format: (v) => String(v),
  },
  'reflected_scene.rotation': {
    label: 'Scene rotation',
    isColor: false,
    group: 'Reflected scene',
    format: (v) =>
      typeof v === 'number' ? `${Math.round(v)}°` : String(v),
  },
  'reflected_scene.distortion': {
    label: 'Scene distortion',
    isColor: false,
    group: 'Reflected scene',
    format: (v) => (typeof v === 'number' ? v.toFixed(2) : String(v)),
  },

  'color_tint.enabled': {
    label: 'Colour tint',
    isColor: false,
    group: 'Colour tint',
    format: (v) => (v ? 'on' : 'off'),
  },
  'color_tint.color': {
    label: 'Tint colour',
    isColor: true,
    group: 'Colour tint',
    format: (v) => String(v),
  },
  'color_tint.intensity': {
    label: 'Tint intensity',
    isColor: false,
    group: 'Colour tint',
    format: (v) => (typeof v === 'number' ? v.toFixed(2) : String(v)),
  },

  emotion: {
    label: 'Emotion',
    isColor: false,
    group: 'Mood',
    format: (v) => String(v),
  },
  gaze_direction: {
    label: 'Gaze',
    isColor: false,
    group: 'Gaze',
    format: (v) => String(v),
  },
  'lighting.enabled': {
    label: 'Lighting',
    isColor: false,
    group: 'Lighting',
    format: (v) => (v ? 'on' : 'off'),
  },
  'lighting.key_angle': {
    label: 'Key light angle',
    isColor: false,
    group: 'Lighting',
    format: (v) => String(v),
  },
  'lighting.quality': {
    label: 'Light quality',
    isColor: false,
    group: 'Lighting',
    format: (v) => String(v),
  },
  'lighting.temperature': {
    label: 'Light temperature',
    isColor: false,
    group: 'Lighting',
    format: (v) => String(v),
  },
  'lighting.intensity': {
    label: 'Light intensity',
    isColor: false,
    group: 'Lighting',
    format: (v) => (typeof v === 'number' ? v.toFixed(2) : String(v)),
  },

  aspect_ratio: {
    label: 'Aspect ratio',
    isColor: false,
    group: 'Technical',
    format: (v) => String(v),
  },
  resolution: {
    label: 'Resolution',
    isColor: false,
    group: 'Technical',
    format: (v) => String(v),
  },
  n_variations: {
    label: 'Variations',
    isColor: false,
    group: 'Technical',
    format: (v) => `${v}×`,
  },
  skin_visibility: {
    label: 'Skin visibility',
    isColor: false,
    group: 'Technical',
    format: (v) => (typeof v === 'number' ? v.toFixed(2) : String(v)),
  },
  bokeh_strength: {
    label: 'Bokeh',
    isColor: false,
    group: 'Technical',
    format: (v) => (typeof v === 'number' ? v.toFixed(2) : String(v)),
  },
};

/** Recursively walks two Parameters objects and emits dot-path diffs. */
export function computeParameterDiff(
  before: Parameters,
  after: Parameters,
): ParameterDiffEntry[] {
  const out: ParameterDiffEntry[] = [];
  walkDiff('', before, after, out);
  return out;
}

function walkDiff(
  prefix: string,
  a: unknown,
  b: unknown,
  out: ParameterDiffEntry[],
): void {
  if (a === b) return;
  if (
    a === null ||
    b === null ||
    typeof a !== 'object' ||
    typeof b !== 'object' ||
    Array.isArray(a) ||
    Array.isArray(b)
  ) {
    out.push({ parameter: prefix, before: a, after: b });
    return;
  }
  const aRec = a as Record<string, unknown>;
  const bRec = b as Record<string, unknown>;
  const keys = new Set<string>([...Object.keys(aRec), ...Object.keys(bRec)]);
  for (const key of keys) {
    walkDiff(prefix ? `${prefix}.${key}` : key, aRec[key], bRec[key], out);
  }
}

/**
 * Used by the diff bubble to render a friendly short summary of what
 * changed, e.g. "Iris colour, Pupil size, Catchlight enabled".
 */
export function summariseDiff(diff: ParameterDiffEntry[]): string {
  if (diff.length === 0) return 'No visible changes';
  const labels = diff
    .map((d) => PARAMETER_LABELS[d.parameter]?.label ?? d.parameter)
    .slice(0, 4);
  if (diff.length <= labels.length) return labels.join(', ');
  return `${labels.join(', ')} +${diff.length - labels.length} more`;
}
